import { useState } from 'react'
import { Download, FileDown } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Select } from '@/components/ui/select'
import { Input } from '@/components/ui/input'
import { useToast } from '@/components/ui/use-toast'
import { useOrganizations } from '@/features/workers/hooks'
import { useEncounters } from '@/features/fichas/hooks'
import { ApiError, apiRequestBlob } from '@/lib/api'
import { todayChile } from '@/lib/utils'

type ExportFormat = 'xlsx' | 'csv'

export default function ExportPage() {
  const { show } = useToast()
  const [organizationId, setOrganizationId] = useState('')
  const [operativoId, setOperativoId] = useState('')
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const [format, setFormat] = useState<ExportFormat>('xlsx')
  const [loading, setLoading] = useState<ExportFormat | null>(null)

  const { data: orgsData } = useOrganizations()
  const { data: encData } = useEncounters({
    organizationId: organizationId || undefined,
  })

  const run = async (fmt: ExportFormat) => {
    setLoading(fmt)
    try {
      const body: Record<string, unknown> = { format: fmt }
      if (operativoId) body.operativo_id = operativoId
      if (organizationId) body.organization_id = organizationId
      if (from) body.from = from
      if (to) body.to = to

      const { blob, headers } = await apiRequestBlob('/api/export/masivo', {
        method: 'POST',
        body,
      })

      const rowCount = Number(headers.get('x-row-count') ?? '0')

      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      const stamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19)
      a.download = `fitlab-export-${stamp}.${fmt}`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)

      show({
        kind: 'success',
        title: 'Exportación lista',
        description: `Se exportaron ${rowCount || '—'} filas en formato ${fmt.toUpperCase()}.`,
      })
    } catch (err) {
      const msg =
        err instanceof ApiError && err.status === 403
          ? 'No tienes permisos para exportar.'
          : 'No se pudo generar la exportación.'
      show({ kind: 'error', title: 'Error de exportación', description: msg })
    } finally {
      setLoading(null)
    }
  }

  const orgOptions = [
    { value: '', label: 'Todas las empresas' },
    ...(orgsData?.items ?? []).map((o) => ({ value: o.id, label: o.name })),
  ]
  const encOptions = [
    { value: '', label: 'Todos los operativos' },
    ...(encData?.items ?? []).map((e) => ({ value: e.operativoId, label: e.operativoId })),
  ]

  return (
    <div className="flex flex-col gap-4" data-testid="export-page">
      <header>
        <h1 className="text-2xl font-bold tracking-tight text-slate-900">Exportaciones masivas</h1>
        <p className="text-sm text-slate-600">
          Descarga fichas clínicas por operativo, empresa o rango de fechas. Cada descarga queda
          registrada en el log de auditoría.
        </p>
      </header>

      <Card>
        <CardHeader>
          <CardTitle>Filtros</CardTitle>
          <CardDescription>
            Todos los filtros son opcionales. Sin filtros exporta todo lo visible para tu rol.
          </CardDescription>
        </CardHeader>
        <CardContent className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Select
            label="Empresa"
            value={organizationId}
            onChange={setOrganizationId}
            options={orgOptions}
            data-testid="export-filter-org"
          />
          <Select
            label="Operativo"
            value={operativoId}
            onChange={setOperativoId}
            options={encOptions}
            data-testid="export-filter-operativo"
          />
          <Input
            label="Desde"
            type="date"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            max={todayChile()}
            data-testid="export-filter-from"
          />
          <Input
            label="Hasta"
            type="date"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            max={todayChile()}
            data-testid="export-filter-to"
          />
          <Select
            label="Formato"
            value={format}
            onChange={(v) => setFormat(v as ExportFormat)}
            options={[
              { value: 'xlsx', label: 'Excel (.xlsx)' },
              { value: 'csv', label: 'CSV (.csv)' },
            ]}
            data-testid="export-filter-format"
          />
        </CardContent>
      </Card>

      <div className="flex flex-col gap-2 sm:flex-row">
        <Button
          onClick={() => run('xlsx')}
          loading={loading === 'xlsx'}
          leftIcon={<FileDown className="h-4 w-4" />}
          data-testid="export-xlsx"
        >
          Descargar XLSX
        </Button>
        <Button
          variant="outline"
          onClick={() => run('csv')}
          loading={loading === 'csv'}
          leftIcon={<Download className="h-4 w-4" />}
          data-testid="export-csv"
        >
          Descargar CSV
        </Button>
      </div>
    </div>
  )
}
